import { useState, useEffect, FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, CheckCircle2, Truck, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, extractMissingRateAction } from '@/lib/api';
import { useAuth } from '@/lib/auth';
// PR-MAY15-FIX-DISAPPEAR-BUG item 2 — replace the native vehicle <select>
// with the searchable VehiclePicker so operators can find a vehicle by
// any plate spelling. Same for DriverPicker (already wired into other
// forms, just not Quick Log yet).
import { VehiclePicker } from '@/components/common/VehiclePicker';
import { DriverPicker } from '@/components/common/DriverPicker';
import { useContracts } from '@/hooks/useSiteContracts';
import { useVehicles } from '@/hooks/useVehicles';
import { useDrivers } from '@/hooks/useDrivers';
import { previewTripMath, useCreateTrip } from '@/hooks/useTrips';
import { LiveMathPreview } from '@/components/contracts/LiveMathPreview';
import type { TripInput, TripPreviewMathResponse } from '@/lib/types';

interface QuickLogFormProps {
  onCreated?: () => void;
  onCancel?: () => void;
}

/**
 * Quick Log — minimal trip entry for daily use.
 * Three required inputs:
 *   1. Contract (auto-populates rates, site, client, trip type)
 *   2. Vehicle (auto-populates driver via "last assignment")
 *   3. Delivered quantity
 * Everything else (receipt photo, signatory, notes) is added later via edit.
 */
export function QuickLogForm({ onCreated, onCancel }: QuickLogFormProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // PR-MAY14-BULK-TRIPS — only super_admin / admin / accountant see
  // the "log N trips" affordance. The backend gate enforces the same
  // set; this hides the UI for everyone else.
  const canBulk = ['super_admin', 'admin', 'accountant'].includes(user?.role ?? '');

  const [contractId, setContractId] = useState<string>('');
  const [vehicleId, setVehicleId]   = useState<string>('');
  const [driverId, setDriverId]     = useState<string>('');
  const [deliveredQty, setDeliveredQty] = useState<number>(0);
  // PR-MAY15-LEDGERS-V3 item 1 — default to LOCAL today, not UTC. Asia/
  // Riyadh is +03:00, so for 3 hours every day (00:00-02:59 local)
  // toISOString().slice(0,10) returns yesterday's date — the operator
  // thinks they're logging today and the trip ends up backdated. Build
  // the YYYY-MM-DD string from the local components instead.
  const [tripDate, setTripDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [tripCount, setTripCount]   = useState<number>(1);    // PR-MAY14-BULK-TRIPS
  const [error, setError]           = useState<string | null>(null);
  // PR-MAY15-INVESTOR-RATES-DRAWER item 3 — when trip-create returns
  // MISSING_INVESTOR_RATE the server includes an action_url pointing to
  // the investor drawer. We capture it here so the banner can render
  // a "Set rate now" link instead of the operator having to navigate
  // by hand.
  const [missingRate, setMissingRate] = useState<{ message: string; actionUrl: string | null } | null>(null);
  // PR-MAY15-FIX-DISAPPEAR-BUG item 1 — success toast / green banner.
  // Shown for ~600ms after a successful save before the drawer closes.
  const [success, setSuccess]       = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview]       = useState<TripPreviewMathResponse | null>(null);

  // Load picklists
  const { data: contractsData } = useContracts({ status: 'active', pageSize: 100 });
  const { data: vehiclesData }  = useVehicles({ pageSize: 100 });
  const { data: driversData }   = useDrivers({ pageSize: 100 });

  const contracts = contractsData?.items ?? [];
  const vehicles  = vehiclesData?.items ?? [];
  const drivers   = driversData?.items ?? [];

  const contract = useMemo(() => contracts.find((c) => c.id === contractId), [contracts, contractId]);

  // When contract changes, default delivered_qty to its targetQty (so previewing
  // shows the "100% delivery" scenario by default).
  useEffect(() => {
    if (contract?.targetQty != null) {
      setDeliveredQty(Number(contract.targetQty));
    }
  }, [contract?.id]);

  // Live math preview from server (authoritative — uses real investor rate)
  useEffect(() => {
    let cancelled = false;
    if (!contractId || !deliveredQty || deliveredQty < 0) {
      setPreview(null);
      return;
    }
    const tm = setTimeout(async () => {
      try {
        const result = await previewTripMath(contractId, deliveredQty);
        if (!cancelled) setPreview(result);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [contractId, deliveredQty]);

  const createMutation = useCreateTrip();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMissingRate(null);
    if (!contractId)         { setError(t('trips.errors.contractRequired')); return; }
    // Driver first: it is what the operator picks, and it usually brings the
    // truck with it.
    if (!driverId)           { setError(t('trips.errors.driverRequired')); return; }
    // Still required by the server - a trip with no truck has no km, no fuel and
    // no cost - but only asked for when the driver has no truck of their own.
    if (!vehicleId) {
      setError('This driver has no vehicle assigned. Pick one, or assign them a truck in Settings → Sides.');
      return;
    }
    if (!deliveredQty || deliveredQty < 0) {
      setError(t('trips.errors.deliveredQtyRequired')); return;
    }
    if (!contract) { setError(t('trips.errors.contractNotFound')); return; }

    const payload: TripInput = {
      tripDate,
      tripType:       contract.tripType,
      siteContractId: contractId,
      vehicleId,
      driverId,
      deliveredQty,
    };

    setSubmitting(true);
    try {
      if (canBulk && tripCount > 1) {
        // PR-MAY14-BULK-TRIPS — server creates N identical trips in one
        // round-trip. Returns { requested, created, failed, trips, failures }
        // with HTTP 207 on partial success.
        const r = await apiClient.post('/api/v1/trips/bulk', {
          count: tripCount,
          trip: payload,
        });
        const body = r.data as { created: number; failed: number; failures?: Array<{ error: string }> };
        if (body.failed > 0) {
          const firstErr = body.failures?.[0]?.error ?? 'unknown';
          // PR-MAY15-FIX-DISAPPEAR-BUG item 1 — surface partial failure as
          // an error AND defer onCreated() so the operator sees the
          // message before the drawer auto-closes.
          setError(`Created ${body.created}/${tripCount}. First failure: ${firstErr}`);
          return;
        }
        setSuccess(`${body.created} trip${body.created === 1 ? '' : 's'} created`);
        // small delay so the green toast paints before the drawer animates out
        setTimeout(() => onCreated?.(), 600);
      } else {
        const result = await createMutation.mutateAsync(payload);
        setSuccess(`Trip ${result?.code ?? 'created'} saved`);
        setTimeout(() => onCreated?.(), 600);
      }
    } catch (err) {
      // PR-MAY15-INVESTOR-RATES-DRAWER item 3 — surface missing-rate
      // errors as a structured banner with a "Set rate now" link so the
      // operator can jump straight to the investor drawer.
      const mr = extractMissingRateAction(err);
      if (mr) {
        setMissingRate({ message: mr.message, actionUrl: mr.actionUrl });
      } else {
        setError(extractErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Trip date + (admin only) bulk N counter */}
        <div className={canBulk ? 'grid grid-cols-[1fr_8rem] gap-3' : 'space-y-1.5'}>
          <div className="space-y-1.5">
            <Label htmlFor="qlf-date">{t('trips.field.date')}</Label>
            <Input
              id="qlf-date"
              type="date"
              value={tripDate}
              onChange={(e) => setTripDate(e.target.value)}
            />
          </div>
          {canBulk && (
            <div className="space-y-1.5">
              {/* PR-MAY14-BULK-TRIPS — admin / accountant can log N identical
                  trips at once (e.g. one tanker doing the same run twice in
                  a day). Backend caps at 50. */}
              <Label htmlFor="qlf-count" title="Create this many identical trips">
                # trips
              </Label>
              <Input
                id="qlf-count"
                type="number"
                min={1}
                max={50}
                value={tripCount}
                onChange={(e) => setTripCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                data-trip-count
              />
            </div>
          )}
        </div>

        {/* Contract picker */}
        <div className="space-y-1.5">
          <Label htmlFor="qlf-contract">{t('trips.field.contract')} *</Label>
          <select
            id="qlf-contract"
            value={contractId}
            onChange={(e) => setContractId(e.target.value)}
            className="w-full h-10 px-3 rounded border border-border bg-background"
          >
            <option value="">{t('trips.field.contractPlaceholder')}</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.site?.nameEn ?? '?'} / {c.client?.nameEn ?? '?'} ({t(`trips.tripType.${c.tripType}`)})
              </option>
            ))}
          </select>
          {contract && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
              <MapPin className="h-3 w-3" />
              {t('trips.field.contractInfo', {
                target: Number(contract.targetQty),
                unit: contract.rateUnit === 'per_m3' ? 'm³'
                    : contract.rateUnit === 'per_ton' ? 'ton'
                    : contract.rateUnit === 'per_km'  ? 'km' : 'trip',
              })}
            </p>
          )}
        </div>

        {/* PR-MAY15-FIX-DISAPPEAR-BUG item 2 — vehicle picker now uses the
            searchable VehiclePicker component (plate-first ranking, NEOM /
            Riyadh grouping). Replaces the native <select>. The previous
            implementation's auto-fill-driver-from-vehicle behavior is
            preserved via the second-arg callback. */}
        <div className="space-y-1.5">
          {/* No star: choosing the driver fills this in. It is still sent -
              it is simply not something to fill in by hand. */}
          <Label htmlFor="qlf-vehicle">
            {t('trips.field.vehicle')}
            <span className="ms-2 text-[11px] font-normal text-muted-foreground">
              fills in from the driver
            </span>
          </Label>
          <VehiclePicker
            value={vehicleId || null}
            onChange={(id, v) => {
              setVehicleId(id ?? '');
              const defaultDriverId = v?.currentDriver?.id;
              if (defaultDriverId && !driverId) setDriverId(defaultDriverId);
            }}
            placeholder={t('trips.field.vehiclePlaceholder') as string}
          />
        </div>

        {/* Driver picker — same swap to the searchable DriverPicker. */}
        <div className="space-y-1.5">
          <Label htmlFor="qlf-driver">{t('trips.field.driver')} *</Label>
          <DriverPicker
            value={driverId || null}
            onChange={(id, d) => {
              setDriverId(id ?? '');
              // The truck this driver is on. Only when the vehicle box is still
              // empty - somebody who deliberately paired a spare driver with a
              // particular truck should not have it undone.
              const theirs = d?.currentVehicle?.id;
              if (theirs && !vehicleId) setVehicleId(theirs);
            }}
            preferredDriverId={null}
            placeholder={t('trips.field.driverPlaceholder') as string}
          />
          {vehicleId && driverId && (vehicles.find((x) => x.id === vehicleId) as { currentDriverId?: string | null } | undefined)?.currentDriverId === driverId && (
            <div className="text-xs text-muted-foreground">↑ Auto-filled from vehicle's default driver. Change if different.</div>
          )}
        </div>

        {/* Delivered quantity */}
        <div className="space-y-1.5">
          <Label htmlFor="qlf-qty">
            {t('trips.field.deliveredQty')} *{' '}
            {contract && (
              <span className="text-xs text-muted-foreground font-normal ms-1">
                ({t('trips.field.target')}: {Number(contract.targetQty)})
              </span>
            )}
          </Label>
          <Input
            id="qlf-qty"
            type="number"
            inputMode="decimal"
            step="0.001"
            min="0"
            value={deliveredQty || ''}
            onChange={(e) => setDeliveredQty(parseFloat(e.target.value) || 0)}
          />
        </div>

        {/* Live math preview */}
        {preview && contract && (
          <div className="pt-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              {t('trips.preview.title')}
            </p>
            <LiveMathPreview
              deliveredQty={deliveredQty}
              targetQty={Number(contract.targetQty)}
              clientRateAtTarget={preview.contract.clientRateAtTarget}
              investorRateAtTarget={preview.contract.investorRateAtTarget}
              driverRateAtTarget={preview.contract.driverRateAtTarget}
              rateUnit={contract.rateUnit}
              compact
            />
          </div>
        )}

        {/* PR-MAY15-INVESTOR-RATES-DRAWER item 3 — missing-rate banner
            with deep link to the investor drawer's Vehicles & Rates
            section. The link opens in the same tab so the operator
            doesn't lose the half-filled trip drawer state — we keep the
            form mounted underneath. */}
        {missingRate && (
          <div className="flex items-start gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 text-sm"
               data-trip-missing-rate-banner>
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p>{missingRate.message}</p>
              {missingRate.actionUrl && (
                <a
                  href={missingRate.actionUrl}
                  className="inline-block font-medium underline underline-offset-2 hover:opacity-80"
                  data-trip-set-rate-now
                >
                  {t('trips.errors.setRateNow')}
                </a>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* PR-MAY15-FIX-DISAPPEAR-BUG item 1 — success toast. Shown until
            the drawer closes (~600ms). Green banner so the operator
            has visual confirmation BEFORE the drawer animates out. */}
        {success && (
          <div className="flex items-start gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-sm"
            data-trip-success-toast>
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}
      </div>

      <div className="border-t border-border p-4 flex items-center justify-end gap-2 shrink-0">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
        )}
        
        <p className="text-xs text-muted-foreground italic">{t('trips.photoHint')}</p>
        <Button type="submit" disabled={submitting}>
          {submitting
            ? <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('common.saving')}</>
            : <><CheckCircle2 className="h-4 w-4 me-2" /> {t('trips.submit')}</>
          }
        </Button>
      </div>
    </form>
  );
}
